import React, { lazy } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';
import { RoutePermissionGuard } from '@shared/auth/RoutePermissionGuard';
import { Role } from '@shared/auth/AuthContext';

// Module-isolated page imports
const StudentDashboardPage = lazy(() => import('./modules/students/pages/StudentDashboardPage'));
const StudentsPage = lazy(() => import('./modules/students/pages/StudentsPage'));
const StudentDetailPage = lazy(() => import('./modules/students/pages/StudentDetailPage'));
const StudentProfilePage = lazy(() => import('./modules/students/pages/StudentProfilePage'));
const RewardsPage = lazy(() => import('./modules/rewards/pages/RewardsPage'));
const EnrollmentsPage = lazy(() => import('./modules/enrollments/pages/EnrollmentsPage'));
const AttendancePage = lazy(() => import('./modules/attendance/pages/AttendancePage'));

// Tuition & Tutors module pages
const HireTutorPage = lazy(() => import('./modules/tuition/pages/HireTutorPage'));
const MyTutorsPage = lazy(() => import('./modules/tuition/pages/MyTutorsPage'));
const TopicMasteryPage = lazy(() => import('./modules/tuition/pages/TopicMasteryPage'));
const TutorsAdminPage = lazy(() => import('./modules/tuition/pages/TutorsAdminPage'));
const TutorDetailPage = lazy(() => import('./modules/tuition/pages/TutorDetailPage'));

const ADMIN_ROLES: Role[] = ['owner', 'admin', 'branch_head'];
const DIRECTORY_ROLES: Role[] = ['owner', 'admin', 'branch_head', 'teacher', 'counselor', 'accountant'];
const ENROLLMENT_ROLES: Role[] = ['owner', 'admin', 'branch_head', 'teacher', 'counselor'];

export const sisRoutes: RouteObject[] = [
  {
    index: true,
    element: <Navigate to="dashboard" replace />,
  },
  {
    path: 'dashboard',
    handle: { crumb: 'Learner Dashboard' },
    element: <StudentDashboardPage />,
  },
  {
    path: 'students',
    handle: { crumb: 'Student Directory' },
    element: (
      <RoutePermissionGuard permission="students.read" allowedRoles={DIRECTORY_ROLES}>
        <StudentsPage />
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'students/:id',
    handle: { crumb: 'Student Dossier' },
    element: <StudentDetailPage />,
  },
  {
    path: 'profile',
    handle: { crumb: 'Student Profile & Pass' },
    element: <StudentProfilePage />,
  },
  {
    path: 'rewards',
    handle: { crumb: 'Rewards & Gamification' },
    element: <RewardsPage />,
  },
  {
    path: 'enrollments',
    handle: { crumb: 'Course Enrollments' },
    element: (
      <RoutePermissionGuard permission="students.read" allowedRoles={ENROLLMENT_ROLES}>
        <EnrollmentsPage />
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'guardians',
    element: <Navigate to="/sis/students" replace />,
  },
  {
    path: 'guardians/:id',
    element: <Navigate to="/sis/students" replace />,
  },
  {
    path: 'attendance',
    handle: { crumb: 'Attendance & Smart Cards' },
    element: <AttendancePage />,
  },
  {
    path: 'hire-tutor',
    handle: { crumb: 'Hire a Tutor' },
    element: <HireTutorPage />,
  },
  {
    path: 'my-tutors',
    handle: { crumb: 'My Tutors & Class OTP' },
    element: <MyTutorsPage />,
  },
  {
    path: 'tutors-admin',
    handle: { crumb: 'Faculty & Tutors Admin' },
    element: (
      <RoutePermissionGuard permission="students.read" allowedRoles={ADMIN_ROLES}>
        <TutorsAdminPage />
      </RoutePermissionGuard>
    ),
  },
  {
    path: 'tutors/:id',
    handle: { crumb: 'Tutor Dossier' },
    element: <TutorDetailPage />,
  },
  {
    path: 'topics',
    handle: { crumb: 'Topic & Syllabus Mastery' },
    element: <TopicMasteryPage />,
  },
];
